import React, { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { Box, Checkbox } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams, ClearAll } from '@utils/route';
import { useBi } from '@components/Hooks/useBi';
import { Language } from '@common/types';
import { useShallowRouter } from '@components/Hooks/useRouter';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import s from './MobileLanguageFilter.module.scss';

interface MobileLanguageFilterProps {
  onClose: () => void;
  onBack: () => void;
  closeModal: () => void;
  clearWasClicked?: ClearAll;
  setClearWasClicked?: (x: ClearAll) => void;
}

export const mobileLanguageFilterDataHook = 'mobile-language-filter';
export const getLangCheckboxDataHook = (langId: string) => `${langId}-checkbox`;

const MobileLanguageFilter = ({
  onClose,
  onBack,
  closeModal,
  clearWasClicked,
  setClearWasClicked,
}: MobileLanguageFilterProps) => {
  const { sessionId } = useBiSessionContext();
  const {
    state: { languages, selectedLanguages, origin, entry, currentPage },
  } = useExploreContext();
  const { t } = useTranslation();
  const logger = useBi();
  const { navigate, router } = useShallowRouter();

  const [languageSelections, setLanguageSelections] = useState<
    Array<{ id: string; label: string; isChecked: boolean }>
  >([]);

  const hasBeenCleared = useMemo(() => {
    return clearWasClicked?.all || clearWasClicked?.language;
  }, [clearWasClicked?.all, clearWasClicked?.language]);

  const numSelectedLanguages = useMemo(() => {
    return _.size(
      _.filter(languageSelections, (language) => language.isChecked),
    );
  }, [languageSelections]);

  useEffect(() => {
    const selectedLanguagesById: Record<Language['id'], Language> = {};
    for (const selectedLangauge of selectedLanguages) {
      selectedLanguagesById[selectedLangauge.id] = selectedLangauge;
    }
    const formattedLanguages = _.map(languages, (language) => ({
      id: language.id,
      label: language.value,
      isChecked: hasBeenCleared
        ? false
        : Boolean(selectedLanguagesById[language.id]),
    }));
    setLanguageSelections(formattedLanguages);
  }, [selectedLanguages, hasBeenCleared, languages]);

  const onChangeLanguageSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.currentTarget.checked;
    const modifiedSelectionId = event.currentTarget.id;
    setLanguageSelections((languageSelections) =>
      languageSelections.map((selection) => {
        if (selection.id === modifiedSelectionId) {
          return { ...selection, isChecked };
        }
        return selection;
      }),
    );
  };

  const onClickApply = () => {
    const lang = languageSelections
      .filter((lang) => lang.isChecked)
      .map((lang) => lang.id)
      .join(',');

    navigate(
      updateQueryParams(
        {
          lang,
        },
        router,
        clearWasClicked,
      ),
    );
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'apply partner lng/location filter',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          location: '',
          languageSelections: lang,
          currentPage,
        }),
      }),
    );
    closeModal();
  };

  const onClickClear = () => {
    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'clear all language filters',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          currentPage,
        }),
      }),
    );
    setClearWasClicked &&
      setClearWasClicked({ ...clearWasClicked!, language: true });
    setLanguageSelections((languageSelections) =>
      languageSelections.map((selection) => {
        return { ...selection, isChecked: false };
      }),
    );
  };

  return (
    <MobileFilterModalLayout
      title={t('mobile-filter-communication-language')}
      dataHook={mobileLanguageFilterDataHook}
      selectedAmount={numSelectedLanguages || 0}
      content={
        <Box
          backgroundColor="white"
          direction="vertical"
          marginLeft="24px"
          marginTop="12px"
        >
          {languageSelections.map((option) => (
            <Checkbox
              dataHook={getLangCheckboxDataHook(option.id)}
              className={s.checkBox}
              size="medium"
              key={option.id}
              id={option.id}
              checked={option.isChecked}
              onChange={onChangeLanguageSelection}
            >
              {option.label}
            </Checkbox>
          ))}
        </Box>
      }
      onApply={onClickApply}
      onClose={onClose}
      onBack={onBack}
      onClear={onClickClear}
    />
  );
};

export default MobileLanguageFilter;
